import { useRef, useState } from 'react';
import { amount, percentFromBps, quantity, unitLabel } from '../lib/format';
import { amountForQty, monogram, parseQtyToMilli, qtyForAmount, toneOf } from '../lib/till';
import { parseRupeesToPaise } from '../lib/payments';
import type { PosProduct } from '../lib/types';
import { Icon } from './icons';
import { cx, Kbd, Modal } from './ui';
import { useT } from '../i18n';

/**
 * The weigh pad, for loose goods.
 *
 * A customer at the vegetable counter asks for "half a kilo of onions" or, just as often,
 * "fifty rupees of tomatoes". The pad takes either: a weight, or a rupee amount that it turns
 * into the weight at today's rate. Presets cover the usual asks, the keypad covers a touch
 * screen, and a keyboard types straight into the box. The amount shown is rate × weight; the
 * pricing engine still prices the line, so the bill and the invoice agree to the paisa.
 */

type Mode = 'weight' | 'amount';

interface Props {
  product: PosProduct;
  /** Opened from "₹50 tomato" in the search box, or a typed weight: start from that. */
  initial?: { mode: Mode; text: string };
  onAdd: (qtyMilli: number) => void;
  onClose: () => void;
}

export function WeighPad({ product, initial, onAdd, onClose }: Props) {
  const t = useT();
  const [mode, setMode] = useState<Mode>(initial?.mode ?? 'weight');
  const [text, setText] = useState(initial?.text ?? '');
  const inputRef = useRef<HTMLInputElement>(null);

  const unit = unitLabel(product.unit);
  const rate = product.sellingPricePaise;
  const qtyMilli = mode === 'weight' ? parseQtyToMilli(text) : qtyForAmount(parseRupeesToPaise(text), rate);
  const linePaise = amountForQty(qtyMilli, rate);
  const overStock = qtyMilli > 0 && qtyMilli > product.stockMilli;
  const ready = qtyMilli > 0;

  const weightPresets = product.unit.toUpperCase() === 'KG' || product.unit.toUpperCase() === 'LTR'
    ? [
        { label: `250 ${unit === 'kg' ? 'g' : 'ml'}`, text: '0.25' },
        { label: `500 ${unit === 'kg' ? 'g' : 'ml'}`, text: '0.5' },
        { label: `1 ${unit}`, text: '1' },
        { label: `2 ${unit}`, text: '2' },
      ]
    : [];
  const amountPresets = [20, 30, 50, 100].map((rupee) => ({ label: `₹${rupee}`, text: String(rupee) }));
  const presets = mode === 'weight' ? weightPresets : amountPresets;

  function press(key: string) {
    setText((current) => {
      if (key === 'del') return current.slice(0, -1);
      if (key === '.' && current.includes('.')) return current;
      const next = current + key;
      // Three decimals for a weight (grams), two for rupees (paise).
      const decimals = next.split('.')[1];
      if (decimals !== undefined && decimals.length > (mode === 'weight' ? 3 : 2)) return current;
      return next.length > 8 ? current : next;
    });
    inputRef.current?.focus();
  }

  function switchMode(next: Mode) {
    if (next === mode) return;
    // Carry the current figure across, so "₹50" becomes "1.25" rather than starting again.
    setText(next === 'weight' ? (qtyMilli > 0 ? quantity(qtyMilli) : '') : linePaise > 0 ? amount(linePaise).replace(/\.00$/, '') : '');
    setMode(next);
    inputRef.current?.focus();
  }

  function add() {
    if (ready) onAdd(qtyMilli);
  }

  const tone = `tone-${toneOf(product.categoryName)}`;

  return (
    <Modal title={t('weigh.title', { name: product.name })} subtitle={t('weigh.subtitle')} onClose={onClose} size="max-w-3xl" testId="weigh-pad" restoreFocus="barcode">
      <div className="grid gap-5 md:grid-cols-[15rem_minmax(0,1fr)] short:grid-cols-[11rem_minmax(0,1fr)] short:gap-3">
        <div className={cx('flex flex-col gap-3 rounded-2xl p-4 short:gap-2 short:p-3', tone)} style={{ background: 'var(--tone-bg)' }}>
          <span className="mono-badge h-14 w-14 bg-white text-lg shadow-sm short:h-10 short:w-10 short:text-base" aria-hidden>
            {monogram(product.name)}
          </span>
          <div>
            <p className="text-xl leading-tight font-extrabold text-slate-900 short:text-base">{product.name}</p>
            {product.localName ? <p className="mt-0.5 text-lg text-slate-600">{product.localName}</p> : null}
            <p className="tabular mt-1 text-sm font-semibold" style={{ color: 'var(--tone-fg)' }}>
              {t('weigh.rate', { rate: amount(rate), unit, percent: percentFromBps(product.gstBps) })}
            </p>
          </div>
          <p className="tabular mt-auto text-xs text-slate-600">
            {t('weigh.onShelf', { qty: quantity(product.stockMilli), unit })}
          </p>
        </div>

        {/* On a short laptop screen the entry and the keypad sit side by side, so Add stays on screen. */}
        <div className="min-w-0 space-y-3 short:grid short:grid-cols-2 short:items-start short:gap-3 short:space-y-0">
          <div className="min-w-0 space-y-3 short:space-y-2">
            <div className="seg" role="group" aria-label={t('weigh.by')}>
              <button type="button" className="seg-btn" aria-pressed={mode === 'weight'} onClick={() => switchMode('weight')} data-testid="weigh-mode-weight">
                <Icon name="scale" size={16} /> {t('weigh.byWeight')}
              </button>
              <button type="button" className="seg-btn" aria-pressed={mode === 'amount'} onClick={() => switchMode('amount')} data-testid="weigh-mode-amount">
                {t('weigh.byAmount')}
              </button>
            </div>

            <label className="block rounded-2xl border-2 px-4 py-3" style={{ borderColor: 'var(--ui-primary)' }}>
              <span className="text-[11px] font-bold tracking-wide text-slate-500 uppercase">
                {mode === 'weight' ? t('weigh.weightIn', { unit }) : t('weigh.askedFor')}
              </span>
              <span className="flex items-baseline gap-3">
                {mode === 'amount' ? <span className="text-4xl font-extrabold text-slate-400">₹</span> : null}
                <input
                  ref={inputRef}
                  className="tabular w-full min-w-0 bg-transparent text-5xl leading-tight font-extrabold text-slate-900 outline-none short:text-4xl"
                  value={text}
                  inputMode="decimal"
                  autoComplete="off"
                  data-autofocus=""
                  data-testid="weigh-input"
                  aria-label={mode === 'weight' ? t('weigh.weightIn', { unit }) : t('weigh.amountAria')}
                  placeholder="0"
                  onChange={(event) => setText(event.target.value.replace(/[^\d.]/g, '').slice(0, 8))}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      add();
                    }
                  }}
                />
                {mode === 'weight' ? <span className="text-2xl font-bold text-slate-400">{unit}</span> : null}
              </span>
              <span className="tabular mt-1 block text-base font-bold" style={{ color: 'var(--ui-primary)' }} data-testid="weigh-result">
                {ready ? (mode === 'weight' ? `= ₹${amount(linePaise)}` : `= ${quantity(qtyMilli)} ${unit} · ₹${amount(linePaise)}`) : ' '}
              </span>
            </label>

            {presets.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {presets.map((preset) => (
                  <button key={preset.label} type="button" className="chip" aria-pressed={text === preset.text} onClick={() => setText(preset.text)}>
                    {preset.label}
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          <div className="min-w-0 space-y-3 short:space-y-2">
            <div className="keypad" aria-label={t('weigh.keypad')}>
              {['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0'].map((key) => (
                <button key={key} type="button" onClick={() => press(key)} aria-label={key === '.' ? t('weigh.decimal') : key}>
                  {key}
                </button>
              ))}
              <button type="button" onClick={() => press('del')} aria-label={t('weigh.backspace')}>
                <Icon name="undo" size={22} className="mx-auto" />
              </button>
            </div>

            {overStock ? (
              <p className="banner banner-warn" role="status">
                {t('weigh.overShelf', { qty: quantity(product.stockMilli), unit })}
              </p>
            ) : null}

            <button
              type="button"
              className="btn btn-primary min-h-14 w-full rounded-xl text-lg short:min-h-12 short:text-base"
              disabled={!ready}
              onClick={add}
              data-testid="weigh-add"
            >
              <Icon name="plus" size={20} strokeWidth={2.4} />
              {ready ? t('weigh.add', { qty: quantity(qtyMilli), unit, amount: amount(linePaise) }) : t('weigh.enter')}
              <Kbd>Enter</Kbd>
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
